import * as LogHelper from '../utility/logger';
import { PermissionType } from './permission.interfaces';

// TODO: Remove all `@ts-ignore`s

/**
 * The hasAccess and hasAdminAccess functions provide a simplified API surrounding asking basic permission level questions for BOOLEAN and SECURITY type attributes.  They are recommended to be used for non-limit type permission checks to abstract the majority of use cases.
 * @param {object[]} securityAttributes A collection of security attributes assigned to the current user.  Can be accessed via Redux using the UserSecurityAttributesSelector from SessionSelectors.
 * @param {object} [securityAttributeDescriptor] A simple descriptor object for the security attribute being checked.  Must contain an id for the security attribute, and a type defined in PermissionType.  ex: { id: 1, type: PermissionType.SECURITY }
 */
export function hasAccess(
  // @ts-ignore : implicit any
  securityAttributes,
  // @ts-ignore : implicit any
  securityAttributeDescriptor
) {
  if (!securityAttributeDescriptor) {
    LogHelper.error(
      'A security attribute descriptor was not provided to PermissionService.hasAccess.  Defaulting to false.'
    );
    return false;
  }

  switch (securityAttributeDescriptor.type) {
    case PermissionType.BOOLEAN:
      return hasBooleanAccess(
        securityAttributes,
        securityAttributeDescriptor.id
      );
    case PermissionType.SECURITY:
      return hasReadAccess(securityAttributes, securityAttributeDescriptor.id);
    default:
      LogHelper.warn(
        'Only types BOOLEAN and SECURITY are handled by PermissionService.hasAccess.  Defaulting to false.'
      );
      return false;
  }
}
export function hasAdminAccess(
  // @ts-ignore : implicit any
  securityAttributes,
  // @ts-ignore : implicit any
  securityAttributeDescriptor
) {
  if (!securityAttributeDescriptor) {
    LogHelper.error(
      'A security attribute descriptor was not provided to PermissionService.hasAdminAccess.  Defaulting to false.'
    );
    return false;
  }
  if (securityAttributeDescriptor.type !== PermissionType.SECURITY) {
    LogHelper.warn(
      'Only type SECURITY is handled by PermissionService.hasAccess.  Defaulting to false.'
    );
    return false;
  }

  return hasFullAccess(securityAttributes, securityAttributeDescriptor.id);
}

/*
    The next series of functions provide more fine level control over asking security permission related questions.  They do not provide any
    safety checks with regards to the type of security attribute, therefore you should know in advance that the attribute you're requesting is
    of an appropriate type for its value.  I.e. calling hasAdminAccess on a LIMIT type attribute will return true for a limit specifically
    set to 2, and will always return false for BOOLEAN types.
*/

export function getSecurityAttributeById(
  // @ts-ignore : implicit any
  securityAttributes,
  // @ts-ignore : implicit any
  securityAttributeId
) {
  // If the current user doesn't have security attributes, return undefined
  if (!securityAttributes || !Array.isArray(securityAttributes)) {
    return undefined;
  }

  return securityAttributes.filter((secAttribute) => {
    return secAttribute.Attribute === securityAttributeId;
  })[0];
}
// @ts-ignore : implicit any
export function hasBooleanAccess(securityAttributes, securityAttributeId) {
  const securityAttribute = getSecurityAttributeById(
    securityAttributes,
    securityAttributeId
  );

  // If there is not a security attribute with the given id, return false
  if (!securityAttribute) {
    return false;
  }

  return Boolean(securityAttribute.Access);
}

export function hasBooleanAccessToAny(
  // @ts-ignore : implicit any
  securityAttributes,
  // @ts-ignore : implicit any
  securityAttributeIds
) {
  // @ts-ignore : implicit any
  return securityAttributeIds.some((item) => {
    return hasBooleanAccess(securityAttributes, item);
  });
}

// @ts-ignore : implicit any
export function hasReadAccess(securityAttributes, securityAttributeId) {
  const securityAttribute = getSecurityAttributeById(
    securityAttributes,
    securityAttributeId
  );

  // If there is not a security attribute with the given id, return false
  if (!securityAttribute) {
    return false;
  }

  // Returns true if the Access value is 1 (read only) or 2 (full-control)
  return securityAttribute.Access > 0;
}
// @ts-ignore : implicit any
export function hasFullAccess(securityAttributes, securityAttributeId) {
  const securityAttribute = getSecurityAttributeById(
    securityAttributes,
    securityAttributeId
  );

  // If there is not a security attribute with the given id, return false
  if (!securityAttribute) {
    return false;
  }

  return securityAttribute.Access === 2;
}

// @ts-ignore : implicit any
export function getUpperLimitAccess(securityAttributes, securityAttributeId) {
  const securityAttribute = getSecurityAttributeById(
    securityAttributes,
    securityAttributeId
  );

  // If there is not a security attribute with the given id, return 0
  if (!securityAttribute) {
    return 0;
  }

  return securityAttribute.Access;
}

// @ts-ignore : implicit any
export function getLowerLimitAccess(securityAttributes, securityAttributeId) {
  const securityAttribute = getSecurityAttributeById(
    securityAttributes,
    securityAttributeId
  );

  // If there is not a security attribute with the given id, return the max int value
  // This really shouldn't happen, but could if the user isn't properly configured somehow.
  if (!securityAttribute) {
    return Number.MIN_SAFE_INTEGER;
  }

  return securityAttribute.Access;
}

// @ts-ignore : implicit any
export function filterOutUnauthorizedCodes(securityAttributes, authCodes) {
  // @ts-ignore : implicit any
  return authCodes.filter((codes) => {
    if (!codes.AdditionalProperties) {
      return true;
    }
    // @ts-ignore : implicit any
    const securityAttribute = codes.AdditionalProperties.filter((attribute) => {
      return attribute.Key === 'SecurityAttribute';
    });
    return (
      securityAttribute.length === 0 ||
      hasAccess(securityAttributes, {
        id: parseInt(securityAttribute[0].Value, 10),
        type: PermissionType.SECURITY
      })
    );
  });
}
